import '@silevis/reactgrid/styles.css';

import { Box, Button, Stack } from '@mui/material';
import {
  Column,
  DefaultCellTypes,
  ReactGrid,
  Row,
  TextCell,
} from '@silevis/reactgrid';
import { useEntityActions } from 'api/entity';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { FinsightEntity, getEntityExtraFieldValue } from 'shared';

export default function SpreadsheetView({
  entities: _entities,
}: {
  entities?: FinsightEntity[];
}) {
  const [columns, setColumns] = useState<Column[]>([]);
  const [rows, setRows] = useState<Row<DefaultCellTypes>[]>([]);
  const [resetKey, setResetKey] = useState(0);
  const entityActions = useEntityActions();

  const entities = useMemo(
    () => _entities?.filter((x) => x.types[0] === _entities[0].types[0]) || [],
    [_entities],
  );

  useEffect(() => {
    // Define default columns
    const defaultColumns: Column[] = [
      { columnId: 'type', width: 150 },
      { columnId: 'name', width: 150 },
      { columnId: 'description', width: 150 },
    ];

    // Get extraFieldsMeta from the first entity if available
    const extraFieldsMeta =
      entities.length > 0 ? entities[0].extraFieldsMeta : {};

    // Create additional columns based on extraFieldsMeta
    const extraColumns: Column[] = Object.keys(extraFieldsMeta || {}).map(
      (key) => ({
        columnId: key,
        width: 150,
      }),
    );

    // Combine default and extra columns
    const newColumns: Column[] = [...defaultColumns, ...extraColumns];
    setColumns(newColumns);

    // Create header row
    const headerRow: Row<DefaultCellTypes> = {
      rowId: 0,
      cells: [
        { type: 'text', text: 'Type', nonEditable: true } as TextCell,
        { type: 'text', text: 'Name', nonEditable: true } as TextCell,
        { type: 'text', text: 'Description', nonEditable: true } as TextCell,
        ...Object.keys(extraFieldsMeta || {}).map(
          (key) =>
            ({
              type: 'text',
              text: extraFieldsMeta[key]?.name || key,
              nonEditable: true,
            }) as TextCell,
        ),
      ],
    };

    // Create data rows from entities
    const dataRows: Row<DefaultCellTypes>[] = entities.map((entity, index) => ({
      rowId: index + 1,
      cells: [
        {
          type: 'text',
          text: entity.types[0] || '',
          nonEditable: true,
        } as TextCell,
        { type: 'text', text: entity.name || '' } as TextCell,
        { type: 'text', text: entity.description || '' } as TextCell,
        ...Object.keys(extraFieldsMeta || {}).map(
          (key) =>
            ({
              type: 'text',
              text: entity.extraFields?.[key]?.toString() || '',
            }) as TextCell,
        ),
      ],
    }));

    // Combine header and data rows
    const newRows: Row<DefaultCellTypes>[] = [headerRow, ...dataRows];

    setRows(newRows);
  }, [entities, resetKey]);

  const handleReset = () => {
    // Trigger re-initialization from parent state
    setResetKey((prev) => prev + 1);
  };

  const handleSave = async () => {
    // Skip header row (index 0)
    const dataRows = rows.slice(1);

    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i];
      const originalEntity = entities[i];

      // Extract cell values
      const cellValues: Record<string, string> = {};
      columns.forEach((col, idx) => {
        const cell = row.cells[idx] as TextCell;
        cellValues[col.columnId as string] = cell.text;
      });

      // Get extra fields from columns beyond the default ones
      const extraFields: Record<string, any> = {};
      const extraFieldsMeta =
        originalEntity?.extraFieldsMeta ||
        (entities.length > 0 ? entities[0].extraFieldsMeta : {});

      columns.slice(3).forEach((col, idx) => {
        const cell = row.cells[idx + 3] as TextCell;
        const fieldKey = col.columnId as string;
        const fieldMeta = extraFieldsMeta?.[fieldKey];
        const cellValue = cell.text;

        extraFields[fieldKey] = getEntityExtraFieldValue(fieldMeta, cellValue);
      });

      if (originalEntity) {
        // Check if the entity has actually changed
        const hasNameChanged = cellValues.name !== originalEntity.name;
        const hasDescriptionChanged =
          cellValues.description !== (originalEntity.description || '');
        const hasExtraFieldsChanged = !_.isEqual(
          extraFields,
          originalEntity.extraFields || {},
        );

        if (hasNameChanged || hasDescriptionChanged || hasExtraFieldsChanged) {
          await entityActions.updateEntity(
            originalEntity.id,
            {
              name: cellValues.name || originalEntity.name,
              description:
                (cellValues.description || originalEntity.description) ??
                undefined,
              extraFields: extraFields,
            },
            originalEntity,
          );
        }
      } else {
        // Create new entity (for newly added rows)
        if (cellValues.type && cellValues.name) {
          await entityActions.createEntity({
            type: cellValues.type,
            name: cellValues.name,
            description: cellValues.description || '',
            extraFields: extraFields,
            parent: entities[0]?.parent || undefined,
          });
        }
      }
    }
  };

  const addRow = () => {
    setRows((prevRows) => {
      const maxRowId = Math.max(...prevRows.map((r) => r.rowId as number), 0);
      const newRow: Row<DefaultCellTypes> = {
        rowId: maxRowId + 1,
        cells: columns.map((col, index) =>
          index === 0
            ? ({
                type: 'text',
                text: entities[0].types[0],
                nonEditable: true,
              } as TextCell)
            : ({ type: 'text', text: '' } as TextCell),
        ),
      };
      return [...prevRows, newRow];
    });
  };

  const addColumn = () => {
    if (true as any) return;
    setColumns((prevColumns) => {
      const newColumnId = `custom_${Date.now()}`;
      return [
        ...prevColumns,
        {
          columnId: newColumnId,
          width: 150,
        },
      ];
    });

    setRows((prevRows) =>
      prevRows.map((row, index) => ({
        ...row,
        cells: [
          ...row.cells,
          index === 0
            ? ({ type: 'text', text: 'New Column' } as TextCell)
            : ({ type: 'text', text: '' } as TextCell),
        ],
      })),
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: 2,
      }}
    >
      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={addRow}>
          Add Row
        </Button>
        <Button variant="contained" onClick={addColumn}>
          Add Column
        </Button>
        <Button variant="contained" color="success" onClick={handleSave}>
          Save
        </Button>
        <Button variant="outlined" onClick={handleReset}>
          Reset
        </Button>
      </Stack>
      <Box
        sx={{
          flex: 1,
          '.react-grid': {
            height: '100%',
            width: '100%',
          },
          '.rg-cell': {
            color: 'white !important',
          },
        }}
      >
        <ReactGrid
          rows={rows}
          columns={columns}
          onCellsChanged={(changes) => {
            setRows((prevRows) => {
              const newRows = [...prevRows];
              changes.forEach((change) => {
                const rowIndex = newRows.findIndex(
                  (row) => row.rowId === change.rowId,
                );
                const columnIndex = change.columnId;
                if (rowIndex !== -1) {
                  const cellIndex = columns.findIndex(
                    (col) => col.columnId === columnIndex,
                  );
                  if (cellIndex !== -1) {
                    newRows[rowIndex].cells[cellIndex] = change.newCell;
                  }
                }
              });
              return newRows;
            });
          }}
          onColumnResized={(columnId, width) => {
            setColumns((prevColumns) =>
              prevColumns.map((col) =>
                col.columnId === columnId ? { ...col, width } : col,
              ),
            );
          }}
          enableRangeSelection
          enableFillHandle
          enableFullWidthHeader
          enableColumnResizeOnAllHeaders
          // enableColumnSelection
          enableGroupIdRender
        />
      </Box>
    </Box>
  );
}
